import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import { Plus, Activity, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  Dialog, Field, FormRow, Tabs, EmptyState, ErrorState, TableSkeleton, OverflowMenu, PriorityPill, StatusPill,
  fmtDate, fmtTime, humanise,
} from '../../components/ui';
import StaffHeader from './StaffHeader';
import { useStaffRecord, useStableCallback, IconButton, toDay } from './hrKit';

const WORKPLACE_TYPES = {
  injury: 'Injury', near_miss: 'Near miss', property_damage: 'Property damage', workplace_hazard: 'Workplace hazard', other: 'Other',
};
const WORKPLACE_STATUS = {
  open: { label: 'Open', cls: 'badge-yellow' },
  under_review: { label: 'Under review', cls: 'badge-blue' },
  closed: { label: 'Closed', cls: 'badge-gray' },
};
const CLIENT_TYPES = {
  fall: 'Fall', medication_error: 'Medication error', behavior: 'Behaviour', property_damage: 'Property damage',
  near_miss: 'Near miss', abuse: 'Abuse or neglect', elopement: 'Absconding', medical_emergency: 'Medical emergency',
};
const dateOf = (v) => (v ? String(v).slice(0, 10) : '');
const showDate = (v) => (v ? fmtDate(dateOf(v)) : '—');
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const byDateDesc = (field) => (a, b) => (toDay(b[field] || b.created_at) || 0) - (toDay(a[field] || a.created_at) || 0);

function IncidentDialog({ open, staffId, incident, onSaved, onClose }) {
  const isEdit = !!incident;
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(incident ? {
      incident_date: dateOf(incident.incident_date), incident_time: incident.incident_time || '', location: incident.location || '',
      incident_type: incident.incident_type || 'other', description: incident.description || '', injury_details: incident.injury_details || '',
      action_taken: incident.action_taken || '', witnesses: incident.witnesses || '', status: incident.status || 'open',
    } : { incident_date: todayKey(), incident_time: '', location: '', incident_type: 'other', description: '', injury_details: '', action_taken: '', witnesses: '', status: 'open' });
  }, [open, incident]);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined })); };
  const close = useStableCallback(() => { if (!saving) onClose(); });

  async function save(e) {
    e.preventDefault();
    const errs = {};
    if (!form.incident_date) errs.incident_date = 'Choose the date it happened.';
    if (!String(form.description || '').trim()) errs.description = 'Describe what happened.';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/staff/${staffId}/incidents/${incident.id}`, form);
      else await api.post(`/staff/${staffId}/incidents`, form);
      toast.success(isEdit ? 'Incident updated' : 'Incident recorded');
      setSaving(false); onSaved(); onClose();
    } catch (err) { setErrors({ form: err.response?.data?.message || "The incident wasn't saved. Try again." }); setSaving(false); }
  }

  return (
    <Dialog open={open} onClose={close} size="lg" title={isEdit ? 'Edit workplace incident' : 'Record workplace incident'}
      footer={(
        <>
          {errors.form && <span className="mr-auto text-[13px] text-rose-700" role="alert">{errors.form}</span>}
          <button type="button" onClick={close} className="btn-secondary" disabled={saving}>Cancel</button>
          <button type="submit" form="staff-incident-form" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Record incident')}</button>
        </>
      )}>
      <form id="staff-incident-form" onSubmit={save} className="space-y-4">
        <FormRow cols={3}>
          <Field label="Date" required error={errors.incident_date}>{(id) => <input id={id} type="date" className="input-field" value={form.incident_date || ''} onChange={(e) => set('incident_date', e.target.value)} />}</Field>
          <Field label="Time">{(id) => <input id={id} type="time" className="input-field" value={form.incident_time || ''} onChange={(e) => set('incident_time', e.target.value)} />}</Field>
          <Field label="Location">{(id) => <input id={id} className="input-field" value={form.location || ''} onChange={(e) => set('location', e.target.value)} />}</Field>
        </FormRow>
        <fieldset>
          <legend className="mb-1.5 text-[13px] font-medium text-slate-700">Type</legend>
          <div className="flex flex-wrap gap-2">
            {Object.entries(WORKPLACE_TYPES).map(([k, label]) => {
              const on = form.incident_type === k;
              return (
                <button key={k} type="button" aria-pressed={on} onClick={() => set('incident_type', k)}
                  className={clsx('h-9 rounded-lg border px-3 text-[13px] font-medium transition-colors duration-150',
                    on ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300')}>
                  {label}
                </button>
              );
            })}
          </div>
        </fieldset>
        <Field label="What happened" required error={errors.description}>{(id) => <textarea id={id} className="input-field" rows={3} value={form.description || ''} onChange={(e) => set('description', e.target.value)} />}</Field>
        <Field label="Injuries">{(id) => <textarea id={id} className="input-field" rows={2} value={form.injury_details || ''} onChange={(e) => set('injury_details', e.target.value)} placeholder="Any injuries, if there were some" />}</Field>
        <Field label="Action taken">{(id) => <textarea id={id} className="input-field" rows={2} value={form.action_taken || ''} onChange={(e) => set('action_taken', e.target.value)} />}</Field>
        <FormRow>
          <Field label="Witnesses">{(id) => <input id={id} className="input-field" value={form.witnesses || ''} onChange={(e) => set('witnesses', e.target.value)} />}</Field>
          <Field label="Status">
            {(id) => (
              <select id={id} className="input-field" value={form.status || 'open'} onChange={(e) => set('status', e.target.value)}>
                {Object.entries(WORKPLACE_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            )}
          </Field>
        </FormRow>
      </form>
    </Dialog>
  );
}

export default function StaffIncidents() {
  const { id } = useParams();
  const confirm = useConfirm();
  const { staff, error: staffError, reload: reloadStaff } = useStaffRecord(id);
  const [incidents, setIncidents] = useState([]);
  const [clientIncidents, setClientIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('all');
  const [tab, setTab] = useState('workplace');

  const load = useCallback(() => {
    setLoading(true); setLoadError(null);
    Promise.all([api.get(`/staff/${id}/incidents`), api.get(`/staff/${id}/client-incidents`)])
      .then(([a, b]) => { setIncidents(Array.isArray(a.data) ? a.data : []); setClientIncidents(Array.isArray(b.data) ? b.data : []); })
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load incidents. Try again."))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const closeDialog = useStableCallback(() => { setDialogOpen(false); setEditing(null); });

  async function deleteIncident(inc) {
    if (!await confirm(`The ${(WORKPLACE_TYPES[inc.incident_type] || 'incident').toLowerCase()} on ${showDate(inc.incident_date)} will be deleted. This can't be undone.`, { title: 'Delete incident?', confirmLabel: 'Delete', danger: true })) return;
    try {
      await api.delete(`/staff/${id}/incidents/${inc.id}`);
      toast.success('Incident deleted');
      setIncidents((list) => list.filter((x) => x.id !== inc.id));
    } catch (e) { toast.error(e.response?.data?.message || "The incident wasn't deleted. Try again."); }
  }

  async function updateStatus(inc, status) {
    try {
      await api.put(`/staff/${id}/incidents/${inc.id}`, {
        incident_date: dateOf(inc.incident_date) || inc.incident_date, incident_time: inc.incident_time || null,
        location: inc.location || null, incident_type: inc.incident_type || 'other', description: inc.description || null,
        injury_details: inc.injury_details || null, action_taken: inc.action_taken || null, witnesses: inc.witnesses || null,
        status,
      });
      toast.success(`Marked ${WORKPLACE_STATUS[status].label.toLowerCase()}`);
      load();
    } catch (e) { toast.error(e.response?.data?.message || "The status wasn't changed. Try again."); }
  }

  const counts = useMemo(() => {
    const c = { all: incidents.length, open: 0, under_review: 0, closed: 0 };
    incidents.forEach((i) => { c[i.status] = (c[i.status] || 0) + 1; });
    return c;
  }, [incidents]);
  const filtered = (filter === 'all' ? incidents : incidents.filter((i) => i.status === filter)).slice().sort(byDateDesc('incident_date'));
  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const box = (child) => <div className="rounded-xl border border-slate-200 bg-white">{child}</div>;

  return (
    <div>
      <StaffHeader staff={staff} error={staffError} onRetry={reloadStaff} />
      <div className="space-y-4">
        <Tabs variant="underline" ariaLabel="Incident type" value={tab} onChange={setTab} tabs={[
          { id: 'workplace', label: 'Workplace incidents', count: loading ? undefined : incidents.length },
          { id: 'client', label: 'Client incidents involved in', count: loading ? undefined : clientIncidents.length },
        ]} />

        {loading ? box(<TableSkeleton rows={3} cols={3} />)
          : loadError ? box(<ErrorState message={loadError} onRetry={load} />)
            : tab === 'workplace' ? (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  <Tabs variant="segmented" size="sm" ariaLabel="Incident status" value={filter} onChange={setFilter} tabs={[
                    { id: 'all', label: 'All', count: counts.all },
                    { id: 'open', label: 'Open', count: counts.open },
                    { id: 'under_review', label: 'Under review', count: counts.under_review },
                    { id: 'closed', label: 'Closed', count: counts.closed },
                  ]} />
                  <button type="button" onClick={openNew} className="btn-primary btn-sm ml-auto"><Plus size={14} aria-hidden="true" />Record incident</button>
                </div>
                {filtered.length === 0 ? box(
                  incidents.length === 0
                    ? <EmptyState icon={Activity} title="No workplace incidents" hint="Record injuries, near misses and hazards this worker was involved in." action={<button type="button" onClick={openNew} className="btn-secondary btn-sm"><Plus size={14} aria-hidden="true" />Record first incident</button>} />
                    : <EmptyState icon={Activity} title={`No ${WORKPLACE_STATUS[filter]?.label.toLowerCase() || ''} incidents`} action={<button type="button" onClick={() => setFilter('all')} className="btn-secondary btn-sm">Show all</button>} />,
                ) : (
                  <ul className="space-y-3">
                    {filtered.map((inc) => {
                      const st = WORKPLACE_STATUS[inc.status] || WORKPLACE_STATUS.open;
                      return (
                        <li key={inc.id} className="rounded-xl border border-slate-200 bg-white">
                          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-slate-900">{WORKPLACE_TYPES[inc.incident_type] || humanise(inc.incident_type) || 'Incident'}</p>
                              <p className="text-[12.5px] text-slate-500">{showDate(inc.incident_date)}{inc.incident_time ? ` · ${fmtTime(inc.incident_time)}` : ''}{inc.location ? ` · ${inc.location}` : ''}</p>
                            </div>
                            <span className={st.cls}>{st.label}</span>
                            <OverflowMenu label="Change status" items={Object.entries(WORKPLACE_STATUS).filter(([k]) => k !== inc.status).map(([k, v]) => ({ label: `Mark ${v.label.toLowerCase()}`, onClick: () => updateStatus(inc, k) }))} />
                            <IconButton label="Edit incident" icon={Pencil} onClick={() => { setEditing(inc); setDialogOpen(true); }} />
                            <IconButton label="Delete incident" icon={Trash2} danger onClick={() => deleteIncident(inc)} />
                          </div>
                          <div className="grid grid-cols-1 gap-4 px-5 py-4 text-sm md:grid-cols-3">
                            <div className="md:col-span-2 space-y-3">
                              <div><h4 className="text-[12px] font-semibold text-slate-500">What happened</h4><p className="mt-0.5 whitespace-pre-wrap leading-relaxed text-slate-700">{inc.description || <span className="text-slate-500">No description.</span>}</p></div>
                              {inc.injury_details && <div><h4 className="text-[12px] font-semibold text-slate-500">Injuries</h4><p className="mt-0.5 whitespace-pre-wrap text-slate-700">{inc.injury_details}</p></div>}
                              {inc.action_taken && <div><h4 className="text-[12px] font-semibold text-slate-500">Action taken</h4><p className="mt-0.5 whitespace-pre-wrap text-slate-700">{inc.action_taken}</p></div>}
                            </div>
                            {inc.witnesses && <div><h4 className="text-[12px] font-semibold text-slate-500">Witnesses</h4><p className="mt-0.5 text-slate-700">{inc.witnesses}</p></div>}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            ) : (
              <>
                <p className="text-[13px] text-slate-600">Client incidents this worker was involved in or reported.</p>
                {clientIncidents.length === 0 ? box(<EmptyState icon={AlertTriangle} title="No client incidents" hint="Incidents on a client's record that name this worker appear here." />) : (
                  <ul className="space-y-3">
                    {[...clientIncidents].sort(byDateDesc('incident_date')).map((inc) => (
                      <li key={inc.id} className="rounded-xl border border-slate-200 bg-white px-5 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <PriorityPill priority={inc.priority || 'medium'} />
                          <span className="text-sm font-semibold text-slate-900">{CLIENT_TYPES[inc.incident_type] || humanise(inc.incident_type) || 'Incident'}</span>
                          {inc.client_name && <span className="text-[13px] text-slate-600">· {inc.client_name}</span>}
                          <span className="ml-auto"><StatusPill status={inc.status || 'open'} /></span>
                        </div>
                        {inc.title && <p className="mt-1.5 text-sm font-medium text-slate-800">{inc.title}</p>}
                        {inc.description && <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{inc.description}</p>}
                        <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 border-t border-slate-100 pt-2 text-[12.5px] text-slate-500">
                          {inc.incident_date && <span>{showDate(inc.incident_date)}</span>}
                          {inc.shift_date && <span>Shift {showDate(inc.shift_date)}{inc.start_time ? ` · ${fmtTime(inc.start_time)}–${fmtTime(inc.end_time)}` : ''}</span>}
                          {inc.reporter_name && <span>Reported by {inc.reporter_name}</span>}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
      </div>
      <IncidentDialog open={dialogOpen} staffId={id} incident={editing} onSaved={load} onClose={closeDialog} />
    </div>
  );
}
